import AddIcon from '@mui/icons-material/Add';
import { Box, Divider, List, Paper, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { HiOutlineBanknotes } from 'react-icons/hi2';
import {
  LuBookOpenCheck,
  LuFileClock,
  LuGraduationCap,
  LuLoader,
  LuMapPin,
  LuStamp,
} from 'react-icons/lu';
import { TbBriefcase } from 'react-icons/tb';
import { JobServeSkill } from 'wisestep-api';
import {
  WsButton,
  WsSelect,
  ControlledTextInput,
  WsTagsList,
  ChipsType,
  WsColors,
} from 'wisestep-base';

import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import TextInputFilter from '../../../../components/filters/components/TextInputFilter';
import WsAccordion from '../../../../components/filters/components/WsAccordion';
import AdvancedFiltersHeader from '../../../../utils/AdvancedFiltersHeader';
import BasicSearchForm from '../../types/IBasicSearchForm';
import JobServeAdvanceSearch from '../types/IJobServeAdvanceSearch';
import {
  employmentTypeOptions,
  experienceOptions,
  industryOptions,
  minEducationOptions,
  salaryOptions,
  securityClearanceCheckBoxOptions,
  skillLevelOptions,
  skillMatchOptions,
  visaStatusOptions,
} from '../utils/JobServeHelper';

interface AdvanceFilterNewProps {
  country?: string;
  performSearch: (values: BasicSearchForm & JobServeAdvanceSearch) => void;
  setSkills: (values: JobServeSkill[]) => void;
  skills: JobServeSkill[];
}

const AdvancedFiltersNew = ({
  country = 'UKEur',
  performSearch,
  setSkills,
  skills,
}: AdvanceFilterNewProps) => {
  const methods = useFormContext();
  const [skillsTag, setSkillsTag] = useState<string[]>([]);
  const skillName = methods.watch('skills.name');
  const skillExperience = methods.watch('skills.experience');

  const addSkill = () => {
    if (skillName && skillExperience) {
      console.log(skillName);
      console.log(skillExperience);
      setSkills([...skills, { skill: skillName, experience: skillExperience }]);
    }
  };

  const removeSkill = (skillTag: string) => {
    console.log(skillTag);
    const skillDetails = skillTag.split(':');
    const index = skills.findIndex(
      (skill) => skill.skill === skillDetails[0] && skill.experience === skillDetails[1]
    );
    skills.splice(index, 1);
    setSkills([...skills]);
  };

  useEffect(() => {
    const tags: string[] = [];
    skills.forEach((skill) => {
      tags.push(skill.skill + ':' + skill.experience);
    });
    setSkillsTag(tags);
  }, [skills]);

  const { handleSubmit, reset } = methods;
  return (
    <FormProvider {...methods}>
      <Paper sx={{ mt: 2 }}>
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <AdvancedFiltersHeader />
          <Divider />
          <List>
            <CheckboxListFilter
              name="locationTypes"
              options={{
                Home: 'Home',
                Preferred: 'Preferred',
                Job: 'Job',
                Activity: 'Activity',
                All: 'All',
              }}
              title="Location Types"
              startIcon={<LuMapPin color={WsColors.TertiaryShades800} />}
            />
            <WsAccordion
              title="Industry"
              startIcon={<LuBookOpenCheck color={WsColors.TertiaryShades800} />}
            >
              <WsSelect
                placeholder="Select Industry"
                name="industry"
                selectArray={industryOptions}
                size="small"
              />
            </WsAccordion>
            <WsAccordion
              title="Employment Type"
              startIcon={<LuFileClock color={WsColors.TertiaryShades800} />}
            >
              <WsSelect
                placeholder="Select Employment Type"
                name="employmentType"
                selectArray={employmentTypeOptions}
                size="small"
              />
            </WsAccordion>
            <WsAccordion
              title="Min Salary"
              startIcon={<HiOutlineBanknotes color={WsColors.TertiaryShades800} />}
            >
              <WsSelect
                placeholder="Select Min Salary"
                name="minSalary"
                selectArray={salaryOptions[country]}
                size="small"
              />
            </WsAccordion>
            <WsAccordion
              title="Max Salary"
              startIcon={<HiOutlineBanknotes color={WsColors.TertiaryShades800} />}
            >
              <WsSelect
                placeholder="Select Max Salary"
                name="maxSalary"
                selectArray={salaryOptions[country]}
                size="small"
              />
            </WsAccordion>
            <WsAccordion
              title="Skills"
              startIcon={<LuBookOpenCheck color={WsColors.TertiaryShades800} />}
            >
              <Box>
                <Stack direction="row" spacing={2} p={2}>
                  <WsSelect name="skillMatch" label="Match" selectArray={skillMatchOptions} />
                  <WsSelect name="skillLevel" label="Level" selectArray={skillLevelOptions} />
                </Stack>
                <Stack direction="row" spacing={3} p={2}>
                  <ControlledTextInput
                    name="skills.name"
                    fullWidth
                    placeholder="Name"
                    variant="outlined"
                    size="small"
                  />
                  <WsSelect name="skills.experience" selectArray={experienceOptions} />
                  <WsButton variant="contained" size="large" type="button" onClick={addSkill}>
                    <AddIcon sx={{ color: '#fff', fontSize: '1.27rem' }} />
                  </WsButton>
                </Stack>
                {skillsTag && !isEmpty(skillsTag) && (
                  <Stack direction="row" spacing={1} p={1}>
                    <WsTagsList
                      chipArray={skillsTag}
                      count={100}
                      type={ChipsType.PRIMARY}
                      allowDelete={true}
                      onDelete={removeSkill}
                    />
                  </Stack>
                )}
              </Box>
            </WsAccordion>
            <WsAccordion
              title="Visa Status"
              startIcon={<LuLoader color={WsColors.TertiaryShades800} />}
            >
              <WsSelect
                placeholder="Select Visa Status"
                name="visaStatus"
                selectArray={visaStatusOptions[country]}
                size="small"
              />
            </WsAccordion>
            <CheckboxListFilter
              name="securityClearance"
              options={securityClearanceCheckBoxOptions}
              title="Security Clearance"
              startIcon={<LuStamp color={WsColors.TertiaryShades800} />}
            />
            <WsAccordion
              title="Min Education"
              startIcon={<LuGraduationCap color={WsColors.TertiaryShades800} />}
            >
              <WsSelect
                placeholder="Select Min Education"
                name="minEducation"
                selectArray={minEducationOptions[country]}
                size="small"
              />
            </WsAccordion>
            <TextInputFilter
              name="latestJobTitle "
              placeholder=""
              title="Latest Job Title "
              startIcon={<TbBriefcase color={WsColors.TertiaryShades800} />}
            />
          </List>
        </Box>
      </Paper>
    </FormProvider>
  );
};

export default AdvancedFiltersNew;
